/*
 * Product Gallery controller (shared)
 *
 * Initialises every [data-product-gallery] on the page, so the product page and
 * the Featured Product section share one implementation. Each gallery is scoped
 * to its own element.
 *
 * Variant-driven image switching: dispatch a 'gallery:go-to-media' CustomEvent
 * on the gallery element with detail { mediaId }. The gallery only reacts if it
 * contains a slide for that media id, so it is safe with multiple galleries.
 *
 * After replacing a gallery's innerHTML (e.g. Section Rendering swap), dispatch
 * 'gallery:reinit' on document to re-bind.
 */
(function () {
  function initProductGallery(gallery) {
    const mainMedia = gallery.querySelector('[data-main-media]');
    const thumbnails = gallery.querySelectorAll('[data-thumbnail-index]');
    const slides = gallery.querySelectorAll('[data-slide-index]');
    const prevBtn = gallery.querySelector('[data-gallery-nav="prev"]');
    const nextBtn = gallery.querySelector('[data-gallery-nav="next"]');
    const currentCounter = gallery.querySelector('[data-gallery-current]');
    const totalCount = slides.length;

    // Start from whichever slide Liquid rendered active, not slide 0. The gallery
    // opens on the selected-or-first-available variant's media, which is often not
    // the first in product.media - assuming 0 here would leave the controller's
    // idea of position disagreeing with what is on screen, so the first prev/next
    // press would jump to the wrong slide.
    const renderedActive = Array.from(slides).findIndex(s => s.classList.contains('is-active'));
    let currentIndex = renderedActive === -1 ? 0 : renderedActive;

    function pauseSlideMedia(slide) {
      const video = slide.querySelector('video');
      if (video) video.pause();
      const iframe = slide.querySelector('iframe');
      if (iframe) {
        const src = iframe.src;
        iframe.src = src;
      }
    }

    function goToSlide(index) {
      // Loop around
      if (index < 0) index = totalCount - 1;
      if (index >= totalCount) index = 0;

      // Pause media in current slide
      if (slides[currentIndex]) {
        pauseSlideMedia(slides[currentIndex]);
      }

      // Update slides
      slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.hidden = !isActive;
      });

      // Update thumbnails
      thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });

      // Update counter
      if (currentCounter) {
        currentCounter.textContent = index + 1;
      }

      currentIndex = index;
    }

    // Listen for variant-driven media switches (scoped to this gallery element)
    function onGoToMedia(e) {
      const mediaId = String(e.detail.mediaId);
      const slideIndex = Array.from(slides).findIndex(s => s.dataset.mediaId === mediaId);
      if (slideIndex !== -1) goToSlide(slideIndex);
    }
    if (gallery._goToMediaHandler) {
      gallery.removeEventListener('gallery:go-to-media', gallery._goToMediaHandler);
    }
    gallery._goToMediaHandler = onGoToMedia;
    gallery.addEventListener('gallery:go-to-media', onGoToMedia);

    // Thumbnail clicks
    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.thumbnailIndex);
        goToSlide(index);
      });
    });

    // Prev/Next button clicks
    if (prevBtn) {
      prevBtn.addEventListener('click', () => goToSlide(currentIndex - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => goToSlide(currentIndex + 1));
    }

    // Keyboard navigation
    if (mainMedia) {
      mainMedia.setAttribute('tabindex', '0');
      mainMedia.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goToSlide(currentIndex - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goToSlide(currentIndex + 1);
        }
      });
    }

    // Touch swipe support
    let touchStartX = 0;
    const SWIPE_THRESHOLD = 50;

    if (mainMedia) {
      mainMedia.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      mainMedia.addEventListener('touchend', (e) => {
        const touchEndX = e.changedTouches[0].screenX;
        const diff = touchStartX - touchEndX;

        if (Math.abs(diff) > SWIPE_THRESHOLD) {
          if (diff > 0) {
            goToSlide(currentIndex + 1); // Swipe left = next
          } else {
            goToSlide(currentIndex - 1); // Swipe right = prev
          }
        }
      }, { passive: true });
    }

    // Thumbnail overflow expand/collapse
    const thumbnailsWrapper = gallery.querySelector('[data-thumbnails-wrapper]');
    if (thumbnailsWrapper) {
      const thumbnailsContainer = thumbnailsWrapper.querySelector('[data-thumbnails]');
      const downNav = thumbnailsWrapper.querySelector('[data-thumb-nav="down"]');

      if (downNav) {
        downNav.addEventListener('click', () => {
          const isExpanded = thumbnailsContainer.classList.toggle('is-expanded');
          downNav.classList.toggle('is-expanded', isExpanded);
        });
      }

      // The gallery can open on a variant whose thumbnail sits in the collapsed
      // overflow - on desktop that thumbnail is display:none, so the rail would
      // show no active item at all. Start expanded in that case.
      if (thumbnailsContainer && thumbnails[currentIndex] &&
          thumbnails[currentIndex].classList.contains('is-overflow')) {
        thumbnailsContainer.classList.add('is-expanded');
        if (downNav) downNav.classList.add('is-expanded');
      }
    }

    // 3D model poster - hide on click to reveal model-viewer
    gallery.querySelectorAll('[data-model-poster]').forEach(poster => {
      poster.addEventListener('click', () => {
        poster.classList.add('is-hidden');
      });
    });

    // Image Zoom functionality
    const zoomModal = gallery.querySelector('[data-zoom-modal]');
    const zoomImage = gallery.querySelector('[data-zoom-image]');
    const zoomClose = gallery.querySelector('[data-zoom-close]');
    const zoomToggles = gallery.querySelectorAll('[data-zoom-toggle]');

    if (zoomModal && zoomImage) {
      const zoomContent = zoomModal.querySelector('.product-gallery__zoom-modal-content');

      // Zoom state
      let zoomScale = 1;
      let originX = 50;
      let originY = 50;
      let panX = 0;
      let panY = 0;

      // Touch tracking
      let initialPinchDist = 0;
      let initialPinchScale = 1;
      let lastTouchX = 0;
      let lastTouchY = 0;
      let touchMoved = false;

      const ZOOM_LEVEL = 2.5;
      const MAX_ZOOM = 4;

      function applyTransform() {
        zoomImage.style.transformOrigin = originX + '% ' + originY + '%';
        zoomImage.style.transform = (panX || panY)
          ? 'translate(' + panX + 'px, ' + panY + 'px) scale(' + zoomScale + ')'
          : 'scale(' + zoomScale + ')';
      }

      function resetZoom() {
        zoomScale = 1;
        originX = 50;
        originY = 50;
        panX = 0;
        panY = 0;
        zoomImage.style.transform = '';
        zoomImage.style.transformOrigin = '';
        zoomImage.classList.remove('is-zoomed');
      }

      // Open modal from toggle buttons
      zoomToggles.forEach(toggle => {
        toggle.addEventListener('click', () => {
          let img = null;

          const stackedItem = toggle.closest('.product-gallery__stacked-item');
          if (stackedItem) {
            img = stackedItem.querySelector('img[data-zoom-url]');
          } else {
            const mainGallery = toggle.closest('.product-gallery__main');
            if (mainGallery) {
              const activeSlide = mainGallery.querySelector('.product-gallery__slide.is-active');
              if (activeSlide) {
                img = activeSlide.querySelector('img[data-zoom-url]');
              }
            }
          }

          if (!img) return;

          const zoomUrl = img.dataset.zoomUrl;
          if (zoomUrl) {
            zoomImage.src = zoomUrl;
            zoomImage.alt = img.alt || '';
            resetZoom();
            zoomModal.showModal();
          }
        });
      });

      // Click/tap to toggle zoom
      zoomImage.addEventListener('click', (e) => {
        if (touchMoved) return;
        e.stopPropagation();

        if (zoomScale > 1) {
          resetZoom();
        } else {
          const rect = zoomImage.getBoundingClientRect();
          originX = ((e.clientX - rect.left) / rect.width) * 100;
          originY = ((e.clientY - rect.top) / rect.height) * 100;
          zoomScale = ZOOM_LEVEL;
          panX = 0;
          panY = 0;
          applyTransform();
          zoomImage.classList.add('is-zoomed');
        }
      });

      // Desktop: mousemove to pan when zoomed
      zoomContent.addEventListener('mousemove', (e) => {
        if (zoomScale <= 1) return;
        const rect = zoomContent.getBoundingClientRect();
        originX = ((e.clientX - rect.left) / rect.width) * 100;
        originY = ((e.clientY - rect.top) / rect.height) * 100;
        panX = 0;
        panY = 0;
        zoomImage.style.transformOrigin = originX + '% ' + originY + '%';
      });

      // Mobile: touch handlers for pinch-to-zoom and drag-to-pan
      function getTouchDistance(t1, t2) {
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      zoomContent.addEventListener('touchstart', (e) => {
        touchMoved = false;

        if (e.touches.length === 2) {
          e.preventDefault();
          initialPinchDist = getTouchDistance(e.touches[0], e.touches[1]);
          initialPinchScale = zoomScale;
        } else if (e.touches.length === 1 && zoomScale > 1) {
          lastTouchX = e.touches[0].clientX;
          lastTouchY = e.touches[0].clientY;
        }
      }, { passive: false });

      zoomContent.addEventListener('touchmove', (e) => {
        e.preventDefault();
        touchMoved = true;

        if (e.touches.length === 2) {
          // Pinch to zoom
          const dist = getTouchDistance(e.touches[0], e.touches[1]);
          const newScale = initialPinchScale * (dist / initialPinchDist);
          zoomScale = Math.min(MAX_ZOOM, Math.max(1, newScale));

          if (zoomScale > 1) {
            // Set origin to pinch midpoint on first pinch from 1x
            if (initialPinchScale <= 1) {
              const imgRect = zoomImage.getBoundingClientRect();
              const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
              const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
              originX = ((midX - imgRect.left) / imgRect.width) * 100;
              originY = ((midY - imgRect.top) / imgRect.height) * 100;
            }
            zoomImage.classList.add('is-zoomed');
            applyTransform();
          } else {
            resetZoom();
          }
        } else if (e.touches.length === 1 && zoomScale > 1) {
          // Drag to pan
          const dx = e.touches[0].clientX - lastTouchX;
          const dy = e.touches[0].clientY - lastTouchY;
          panX += dx;
          panY += dy;
          lastTouchX = e.touches[0].clientX;
          lastTouchY = e.touches[0].clientY;

          // Clamp pan to keep image reasonably in view
          const maxPanX = (zoomScale - 1) * zoomImage.offsetWidth / 2;
          const maxPanY = (zoomScale - 1) * zoomImage.offsetHeight / 2;
          panX = Math.min(maxPanX, Math.max(-maxPanX, panX));
          panY = Math.min(maxPanY, Math.max(-maxPanY, panY));

          applyTransform();
        }
      }, { passive: false });

      zoomContent.addEventListener('touchend', (e) => {
        if (e.touches.length < 2) {
          initialPinchDist = 0;
        }
      });

      // Close on button click
      if (zoomClose) {
        zoomClose.addEventListener('click', () => {
          zoomModal.close();
        });
      }

      // Close on backdrop click
      zoomModal.addEventListener('click', (e) => {
        if (e.target === zoomModal) {
          zoomModal.close();
        }
      });

      // Reset state on close
      zoomModal.addEventListener('close', () => {
        zoomImage.src = '';
        resetZoom();
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-product-gallery]').forEach(gallery => {
      if (gallery._galleryReady) return;
      gallery._galleryReady = true;
      initProductGallery(gallery);
    });
  }

  function reinitAll() {
    // innerHTML swaps reuse the same gallery element, so clear the flag to force
    // a fresh bind against the new child nodes.
    document.querySelectorAll('[data-product-gallery]').forEach(gallery => {
      gallery._galleryReady = false;
    });
    initAll();
  }

  // Guard against the asset being included more than once on a page.
  if (!window.__productGalleryControllerLoaded) {
    window.__productGalleryControllerLoaded = true;
    document.addEventListener('gallery:reinit', reinitAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
